import { type ReactNode, useEffect, useId, useState } from 'react';
import { type UseQueryResult } from '@tanstack/react-query';
import {
  ActionableNotification,
  Button,
  Dropdown,
  InlineLoading,
  InlineNotification,
  Tab,
  TabList,
  Tabs,
  Toggle,
} from '@carbon/react';
import { Filter } from '@carbon/icons-react';
import { FormattedCount } from 'components/formatted-count';
import { WorkflowStatusPopover } from 'components/workflow-status-popover/workflow-status-popover';
import {
  NpvWaterfall,
  type NpvWaterfallProps,
} from '../npv-waterfall/npv-waterfall';
import { PropertyExportButton } from '../property-export-button/property-export-button';
import { SummaryTable } from '../summary-table/summary-table';
import { useFactorSlots, useGroupFactors, useSlotReveal } from '../../hooks';
import {
  RESCAT_OPTIONS,
  rescatEmptyMessage,
  rescatOption,
  isRunLive,
  rescatExportConfig,
  rescatScopeSuffix,
  toStatusItems,
} from '../../utils';
import {
  SHOW_UNCHANGED_SLOTS_LABEL,
  hasOtherSlotsBar,
  noSlotChangedNotice,
  slotRevealKey,
  slotWindowKey,
} from '../../slot-reveal-utils';
import type {
  ChartExportConfig,
  DrilldownWaterfallResponse,
  EmptyNotice,
  RescatOption,
  RevealedSlotWaterfall,
  RunResponse,
  WaterfallBar,
} from '../../types';
import styles from './comparison-results.module.scss';

/** Stable empty drillable-keys (leaf level) — keeps NpvWaterfall's memo from busting. */
const NO_DRILL: string[] = [];

/** What a level with nothing to draw says by default. */
const NO_FURTHER_DETAIL: EmptyNotice = {
  title: 'No breakdown available',
  subtitle: 'This level has no further detail.',
};

/** Stable empty bars: a level drawing nothing in place of its query's own. */
const NO_BARS: WaterfallBar[] = [];

/**
 * Where in the group → factor → slot drill-down the user is. Owned by the Results page
 * (so the breadcrumb reflects it); the labels ride along so the breadcrumb needs no
 * re-query. `factorKey` null = at the group's factor level; set = at the slot level.
 */
export interface DrillState {
  groupKey: string;
  groupLabel: string;
  factorKey: string | null;
  factorLabel: string | null;
}

/**
 * The label for a bar key, from a level's bars (falls back to the key).
 *
 * @param {WaterfallBar[] | undefined} bars - The bars to search.
 * @param {string} key - The bar key to label.
 * @return {string} The bar's label, or the key when not found.
 */
function labelFor(bars: WaterfallBar[] | undefined, key: string): string {
  return bars?.find((bar) => bar.key === key)?.label ?? key;
}

interface DrillLevelProps {
  banner: ReactNode;
  query: UseQueryResult<DrilldownWaterfallResponse, Error>;
  drillableKeys: string[];
  onDrill?: (key: string) => void;
  /** The level chart's image-export title and filename prefix. */
  exportConfig: ChartExportConfig;
  /** Controls at the banner's end. */
  actions?: ReactNode;
  /** Bars to draw in place of the query's own (e.g. its slots with more revealed). */
  waterfall?: WaterfallBar[];
  /** The chart's window and extend wiring, passed through to the waterfall. */
  chartProps?: Pick<
    NpvWaterfallProps,
    'extendable' | 'initialVisibleCount' | 'onExtend' | 'windowKey'
  >;
  /** What to say when the level has nothing to draw (default: it has no further detail). */
  emptyNotice?: EmptyNotice;
}

/**
 * One drill-down level (group→factors or factor→slots): a context banner over the
 * fetched waterfall, with the level's own loading/error/empty states. A level that never loaded
 * offers Try again (a 503 is an input that couldn't be read just now); one that fails a later
 * refresh keeps its chart, since a finished run's breakdown doesn't change. Mounted once per
 * level (keyed by the caller), it takes keyboard focus when it opens if the control that opened it
 * left with the previous level — a chart's drill button, or a breadcrumb that became the current
 * crumb — so a keyboard user carries on from the new level rather than the top of the page.
 *
 * @param {DrillLevelProps} props - The banner and its actions, the level's query (or bars in its
 *   place), drill wiring, export config, the chart's window wiring, and its empty notice.
 * @return {JSX.Element} The drill-down level.
 */
function DrillLevel({
  banner,
  query,
  drillableKeys,
  onDrill,
  exportConfig,
  actions,
  waterfall,
  chartProps,
  emptyNotice = NO_FURTHER_DETAIL,
}: DrillLevelProps) {
  // The banner outlives the error, loading and loaded states, so keyboard focus waits there
  // while a retry swaps them.
  const bannerId = useId();
  useEffect(() => {
    if (document.activeElement === document.body) {
      document.getElementById(bannerId)?.focus({ preventScroll: true });
    }
  }, [bannerId]);
  const bars = waterfall ?? query.data?.waterfall ?? [];
  return (
    <>
      <div id={bannerId} tabIndex={-1} className={styles.drillBanner}>
        {/* A wrapper keeps the banner's words one flex item, so the spaces around <strong> hold. */}
        <span className={styles.drillBannerText}>{banner}</span>
        {actions}
      </div>
      {query.isLoading ? (
        <InlineLoading description="Loading breakdown…" />
      ) : query.isError && !query.data ? (
        <ActionableNotification
          inline
          kind="error"
          // hasFocus off: its auto-focus would scroll the page to the notice
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the breakdown"
          subtitle={query.error?.message ?? 'Something went wrong.'}
          actionButtonLabel="Try again"
          onActionButtonClick={() => {
            document.getElementById(bannerId)?.focus();
            query.refetch();
          }}
        />
      ) : query.data && bars.length > 0 ? (
        <NpvWaterfall
          bars={bars}
          drillableKeys={drillableKeys}
          onDrill={onDrill}
          height={460}
          exportConfig={exportConfig}
          {...chartProps}
        />
      ) : (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title={emptyNotice.title}
          subtitle={emptyNotice.subtitle}
        />
      )}
    </>
  );
}

interface SlotRevealStatusProps {
  /** How far through the slot list the chart reaches. */
  reveal: RevealedSlotWaterfall;
  /** Slots past the capped chart's own asked for so far (0 = the capped chart). */
  revealedCount: number;
  /** A page a reveal is waiting for is on its way. */
  loading: boolean;
  /** The rows the reveal needs failed to load, and aren't being asked for again. */
  failed: boolean;
  /** The page shows the slots that didn't change (else the list holds only changes). */
  showUnchanged: boolean;
  /** Ask again for what failed: the next page, or the list itself. */
  onRetry: () => void;
}

/**
 * A slot drill-down's reveal status, at its banner's end: that dragging the chart past its end
 * shows more slots, how far through the slot list it reaches, that a page is loading, or that one
 * failed — with Try again, which hands keyboard focus to the status line first, since the button
 * leaves once the page loads. Dragging is pointer-only; the Summary Table lists every slot for
 * anyone who can't drag.
 *
 * @param {SlotRevealStatusProps} props - The reveal, how far it was asked to go, whether $0 slots
 *   are listed, and the list's loading / failure state with its retry.
 * @return {JSX.Element} The status line.
 */
function SlotRevealStatus({
  reveal,
  revealedCount,
  loading,
  failed,
  showUnchanged,
  onRetry,
}: SlotRevealStatusProps) {
  const statusId = useId();

  // The slots counted: only those with a change while the $0 ones are hidden.
  const changedOnly = showUnchanged ? '' : ' with a change';

  let status: ReactNode;
  if (loading) {
    status = (
      <InlineLoading
        // The wrapper below is the live region.
        aria-live="off"
        className={styles.slotLoading}
        description="Loading more slots…"
      />
    );
  } else if (failed) {
    status = (
      <span className={styles.slotStatus}>
        Couldn&apos;t load more slots.
        <Button
          kind="ghost"
          size="sm"
          onClick={() => {
            document.getElementById(statusId)?.focus();
            onRetry();
          }}
        >
          Try again
        </Button>
      </span>
    );
  } else if (reveal.complete && reveal.order === 'impact') {
    // The capped chart with nothing left to reveal: its slot list came back empty.
    status = <span className={styles.slotStatus}>Biggest changes first</span>;
  } else if (reveal.complete) {
    status = (
      <span className={styles.slotStatus}>
        Showing all <FormattedCount value={reveal.shownCount} /> slots
        {changedOnly}, highest to lowest
      </span>
    );
  } else if (revealedCount > 0 && reveal.totalCount > 0) {
    status = (
      <span className={styles.slotStatus}>
        Showing <FormattedCount value={reveal.shownCount} /> of{' '}
        <FormattedCount value={reveal.totalCount} /> slots
        {changedOnly}, highest to lowest · drag left for more
      </span>
    );
  } else {
    status = (
      <span className={styles.slotStatus}>
        Biggest changes first · drag the chart left to see every slot
        {changedOnly}, highest to lowest
      </span>
    );
  }

  return (
    <div className={styles.drillActions}>
      {/* No accessible name: a live region with one may be announced by its name, not its text. */}
      <div id={statusId} tabIndex={-1} role="status">
        {status}
      </div>
    </div>
  );
}

interface ComparisonResultsProps {
  /** The comparison run whose group-level overview + drill-downs to render. */
  run: RunResponse;
  /** The current drill level (owned by the page, reflected in its breadcrumb). */
  drill: DrillState | null;
  /** Drill DOWN into a bar (the page's breadcrumb pops back up). */
  onDrill: (drill: DrillState) => void;
  /** The page's reserve-category filter (null = All Rescats); scopes every level. */
  rescat: string | null;
  /** Change the reserve-category filter (null = All Rescats). */
  onRescatChange: (rescat: string | null) => void;
  /** A category switch is loading (the previous category's results are still shown). */
  isRescatPending?: boolean;
  /** The last category switch failed — the shown results are the previous category's. */
  rescatError?: Error | null;
}

/**
 * What the waterfall view needs: the run, the page's drill, and the category the shown run is
 * scoped to (`run.rescat` — not the selection, which leads it while a switch loads or after
 * one fails).
 */
interface WaterfallViewProps extends Pick<
  ComparisonResultsProps,
  'run' | 'drill' | 'onDrill'
> {
  /** The shown run's reserve category (null = all); scopes the drill fetches. */
  rescat: string | null;
  /** The run is still calculating: hold the chart as a skeleton (see `isRunLive`). */
  live: boolean;
  /** Draw the slots that didn't change too (the page's "Show $0 slots" switch). */
  showUnchanged: boolean;
}

/**
 * The results' "Show $0 slots" switch: off, every slot level — a drill-down chart and the Summary
 * Table's slot lists — leaves out the slots that didn't change; on, they're back. Its label
 * stays visible beside it, as the toolbar's other controls read, and toggles it when clicked.
 *
 * @param {object} props - The switch's props.
 * @param {boolean} props.toggled - $0 slots are shown.
 * @param {Function} props.onToggle - Fired with the new setting.
 * @return {JSX.Element} The labelled switch.
 */
function UnchangedSlotsToggle({
  toggled,
  onToggle,
}: {
  toggled: boolean;
  onToggle: (toggled: boolean) => void;
}) {
  const id = useId();
  return (
    <div className={styles.unchangedToggle}>
      <label htmlFor={id} className={styles.unchangedToggleLabel}>
        {SHOW_UNCHANGED_SLOTS_LABEL}
      </label>
      <Toggle
        id={id}
        size="sm"
        labelA="Off"
        labelB="On"
        toggled={toggled}
        onToggle={onToggle}
      />
    </div>
  );
}

/**
 * The results toolbar (Andrew's design: under the view tabs): a reserve-category (Rescat)
 * dropdown that scopes every level of the results — with, right beside it while the run is still
 * calculating, its status icon (which opens the stage checklist) — and a right-aligned label
 * echoing the scope the shown results actually carry, then the results' actions (the "Show $0
 * slots" switch, Export by property). The drill path lives in the page breadcrumb, not here.
 *
 * @param {object} props - The toolbar's props.
 * @param {string | null} props.rescat - The selected category (null = All Rescats).
 * @param {string | null} props.shownRescat - The category of the results on screen (lags
 *   `rescat` while a switch loads or after one fails).
 * @param {Function} props.onChange - Fired with the new category (null = All Rescats).
 * @param {boolean} props.isPending - A category switch is loading.
 * @param {ReactNode} [props.status] - The live run's status icon, if the run is still calculating.
 * @param {ReactNode} [props.actions] - Controls at the toolbar's right end, after the scope label.
 * @return {JSX.Element} The toolbar.
 */
function RescatToolbar({
  rescat,
  shownRescat,
  onChange,
  isPending,
  status,
  actions,
}: {
  rescat: string | null;
  shownRescat: string | null;
  onChange: (rescat: string | null) => void;
  isPending: boolean;
  status?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={styles.toolbar}>
      <Filter className={styles.toolbarIcon} aria-hidden />
      <Dropdown<RescatOption>
        id="rescat-filter"
        className={styles.rescatDropdown}
        size="sm"
        label="Rescat"
        titleText="Rescat"
        hideLabel
        items={RESCAT_OPTIONS}
        selectedItem={rescatOption(rescat)}
        itemToString={(item) => item?.label ?? ''}
        onChange={({ selectedItem }) => onChange(selectedItem?.key ?? null)}
      />
      {status}
      <span className={styles.toolbarSpacer} />
      {isPending && <InlineLoading description="Updating…" />}
      <span className={styles.scopeLabel} role="status">
        {rescatOption(shownRescat).label}
      </span>
      {actions}
    </div>
  );
}

/**
 * The waterfall view: the NPV value-driver chart at the page's current drill level, with
 * its group → factor → slot drill-down. The overview shows one bar per group; clicking a
 * group fetches its factors, clicking a factor fetches its per-slot impact. Drill state
 * is owned by the page and shown in its breadcrumb (which pops levels back up); this view
 * only renders the current level and drills deeper. A still-running run holds a chart skeleton
 * until every factor is in — no partial bars. Every level is scoped to the shown run's reserve
 * category, and a slot level leaves out its $0 slots unless the page shows them.
 *
 * @param {WaterfallViewProps} props - The run, drill state/handler, shown category, and whether
 *   $0 slots are shown.
 * @return {JSX.Element} The active chart level.
 */
function WaterfallView({
  run,
  drill,
  onDrill,
  rescat,
  live,
  showUnchanged,
}: WaterfallViewProps) {
  // The group query backs the factor level only; it's disabled at the slot level (the
  // breadcrumb labels ride in the drill state, so it isn't needed deeper) and idle at
  // the overview. The slot query is enabled only at the leaf. Both are scoped to the shown
  // run's reserve category, so a drill always matches the overview it came from.
  const groupQuery = useGroupFactors(
    run.id,
    drill && !drill.factorKey ? drill.groupKey : null,
    rescat,
    showUnchanged
  );
  const slotQuery = useFactorSlots(
    run.id,
    drill?.factorKey ?? null,
    rescat,
    showUnchanged
  );

  // A slot chart (a factor's slots, or Slots Removed / Added at the group level) opens capped and
  // reveals more of its slot list each time it's dragged past its end (see useSlotReveal).
  const levelKey = slotRevealKey(run.id, drill, rescat, showUnchanged);
  const cappedBars = (drill?.factorKey ? slotQuery : groupQuery).data
    ?.waterfall;
  const slots = useSlotReveal({
    runId: run.id,
    levelKey,
    listKey: drill?.factorKey ?? drill?.groupKey ?? '',
    rescat,
    includeUnchanged: showUnchanged,
    cappedBars,
  });
  const revealed = slots.revealedCount > 0;

  // Only a slot chart with slots left out (its "All other slots" bar) reveals more.
  const slotActions =
    slots.reveal && (revealed || hasOtherSlotsBar(cappedBars)) ? (
      <SlotRevealStatus
        reveal={slots.reveal}
        revealedCount={slots.revealedCount}
        loading={slots.loading}
        failed={slots.failed}
        showUnchanged={showUnchanged}
        onRetry={slots.retry}
      />
    ) : undefined;
  // A chart that can grow opens on its capped bars and keeps the user's view as slots are added;
  // each list — category, $0 slots shown or not, and order — is its own window (slotWindowKey).
  const slotChartProps =
    cappedBars && (slots.extendable || revealed)
      ? {
          extendable: slots.extendable,
          onExtend: slots.revealMore,
          initialVisibleCount: cappedBars.length,
          windowKey: slotWindowKey(
            rescat,
            showUnchanged,
            slots.reveal?.order ?? 'impact'
          ),
        }
      : undefined;
  // A slot level where no slot changed, with those left out, says so (and how to see them)
  // rather than draw two bare anchors.
  const slotEmptyNotice = noSlotChangedNotice(
    drill,
    rescat,
    showUnchanged,
    cappedBars
  );
  const revealedBars = revealed ? slots.reveal?.bars : undefined;
  // What either slot level (a factor's slots, or Slots Removed / Added) hands its DrillLevel.
  const slotLevelProps = {
    actions: slotActions,
    waterfall: slotEmptyNotice ? NO_BARS : revealedBars,
    chartProps: slotChartProps,
    emptyNotice: slotEmptyNotice,
  };

  let level: ReactNode;
  if (drill?.factorKey) {
    level = (
      <DrillLevel
        key={`factor:${drill.factorKey}`}
        banner={
          <>
            Slot-level impact for <strong>{drill.factorLabel}</strong>
            {rescatScopeSuffix(rescat)} — each shared slot&apos;s contribution
            to the change.
          </>
        }
        query={slotQuery}
        drillableKeys={NO_DRILL}
        exportConfig={rescatExportConfig(
          `Slot-level impact — ${drill.factorLabel ?? drill.factorKey}`,
          `npv-slot-impact-${drill.factorKey}`,
          rescat
        )}
        {...slotLevelProps}
      />
    );
  } else if (drill) {
    level = (
      <DrillLevel
        key={`group:${drill.groupKey}`}
        banner={
          <>
            Factor contributions within <strong>{drill.groupLabel}</strong>
            {rescatScopeSuffix(rescat)} — click a factor for its slot-level
            detail.
          </>
        }
        query={groupQuery}
        drillableKeys={groupQuery.data?.drillableKeys ?? NO_DRILL}
        exportConfig={rescatExportConfig(
          `NPV value drivers — ${drill.groupLabel}`,
          `npv-value-drivers-${drill.groupKey}`,
          rescat
        )}
        {...slotLevelProps}
        onDrill={(key) =>
          onDrill({
            groupKey: drill.groupKey,
            groupLabel: drill.groupLabel,
            factorKey: key,
            factorLabel: labelFor(groupQuery.data?.waterfall, key),
          })
        }
      />
    );
  } else {
    level = (
      <>
        {/* No partial numbers: a run still calculating holds a chart skeleton until every
            factor is in (its stages ride in the toolbar). */}
        {live && (
          <NpvWaterfall bars={run.waterfall} drillableKeys={NO_DRILL} loading />
        )}
        {!live && rescat && run.waterfall.length === 0 && (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title={rescatEmptyMessage(rescat)}
          />
        )}
        {!live && run.waterfall.length > 0 && (
          <NpvWaterfall
            bars={run.waterfall}
            drillableKeys={run.drillableKeys}
            exportConfig={rescatExportConfig(
              'NPV value drivers',
              'npv-value-drivers',
              rescat
            )}
            onDrill={(key) =>
              onDrill({
                groupKey: key,
                groupLabel: labelFor(run.waterfall, key),
                factorKey: null,
                factorLabel: null,
              })
            }
          />
        )}
      </>
    );
  }

  return <div className={styles.chartBlock}>{level}</div>;
}

/**
 * The Results view: a Waterfall / Summary table / Both switch over the same run, with the
 * reserve-category toolbar under the tabs. The chart is the page-driven drill
 * (breadcrumb-owned); the table is the tabular twin with its own independent expansion —
 * both read the same waterfall + drill cache, scoped to the page's category, and both leave out
 * the slots that didn't change until the toolbar's "Show $0 slots" switch is on. "Both" is
 * the default. Views render conditionally (not via TabPanels) so the chart mounts a single
 * SciChart surface.
 *
 * @param {ComparisonResultsProps} props - The run, drill state/handler, and category filter.
 * @return {JSX.Element} The results view.
 */
export function ComparisonResults({
  run,
  drill,
  onDrill,
  rescat,
  onRescatChange,
  isRescatPending = false,
  rescatError = null,
}: ComparisonResultsProps) {
  const [view, setView] = useState(2); // 0 Waterfall · 1 Summary table · 2 Both
  const [showUnchanged, setShowUnchanged] = useState(false);
  const showChart = view === 0 || view === 2;
  const showTable = view === 1 || view === 2;
  // Everything below the toolbar follows the category the shown run carries.
  const shownRescat = run.rescat;
  // A run still calculating holds its chart and table as skeletons until it completes (no
  // partial numbers). Its status icon beside the Rescat dropdown opens the stages either way: a
  // spinner while live, a check once done — kept in place, so nothing jumps and focus stays put.
  const live = isRunLive(run.status);

  return (
    <div className={styles.results}>
      <Tabs
        selectedIndex={view}
        onChange={({ selectedIndex }) => setView(selectedIndex)}
      >
        <TabList aria-label="Results view" contained>
          <Tab>Waterfall</Tab>
          <Tab>Summary table</Tab>
          <Tab>Both</Tab>
        </TabList>
      </Tabs>
      <RescatToolbar
        rescat={rescat}
        shownRescat={shownRescat}
        onChange={onRescatChange}
        isPending={isRescatPending}
        status={
          <WorkflowStatusPopover
            statusItems={toStatusItems(run.progress)}
            overallStatus={live ? 'processing' : 'processed'}
            iconDescription="Show comparison stages"
            ariaLabel="Show comparison stages"
            size="sm"
            showStatusIcon
          />
        }
        actions={
          <>
            <UnchangedSlotsToggle
              toggled={showUnchanged}
              onToggle={setShowUnchanged}
            />
            {/* The category on screen, so the file matches the page; held while there's
                nothing settled to export. */}
            <PropertyExportButton
              runId={run.id}
              rescat={shownRescat}
              disabled={live || isRescatPending || run.waterfall.length === 0}
            />
          </>
        }
      />
      {/* Screen readers hear the run's state change — the page itself shows no progress text. */}
      <span className={styles.visuallyHidden} aria-live="polite">
        {live
          ? 'Comparison in progress. The results appear once every factor is calculated.'
          : 'Comparison complete.'}
      </span>
      {rescatError && (
        <div className={styles.rescatError}>
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={`Couldn't load ${rescatOption(rescat).label}`}
            subtitle={`${rescatError.message} Showing the previous results.`}
          />
          <Button kind="ghost" size="sm" onClick={() => onRescatChange(null)}>
            Show All Rescats
          </Button>
        </div>
      )}
      {showChart && (
        <WaterfallView
          run={run}
          drill={drill}
          onDrill={onDrill}
          rescat={shownRescat}
          live={live}
          showUnchanged={showUnchanged}
        />
      )}
      {showTable && (
        <div className={styles.tableBlock}>
          {/* Keyed by run: the Results page stays mounted across a ?run_id= change, and a
              prior run's expanded rows must not carry over (the page resets the chart's drill
              the same way). */}
          <SummaryTable
            key={run.id}
            run={run}
            rescat={shownRescat}
            loading={live}
            showUnchanged={showUnchanged}
          />
        </div>
      )}
    </div>
  );
}
